import React, { createRef, useEffect } from 'react';

import { BarGraph } from './barGraph';
import gsap from 'gsap';

export const StatisticPanel = ({
    showDollar,
    icon,
    value,
    unit,
    label,
    title,
    description,
    className,
    style,
    graphData,
    tableDataPrevYear,
    tableDataCurrentYear,
    isFlipped,
}) => {
    const statContainer = createRef();
    const front = createRef();
    const frontBG = createRef();
    const backInner = createRef();
    const back = createRef();
    const isToggled = createRef(false);
    const offset = isFlipped ? 150 : -150;
    const hoverOffset = isFlipped ? -8 : 8;

    useEffect(() => {
        const container = statContainer.current;
        gsap.set(back.current, { translateX: offset, opacity: 0 });
        back.current.setAttribute('tabindex', -1);

        const handleMouseEnter = () => {
            if (!frontBG.current || window.innerWidth < 768) return;
            gsap.to(frontBG.current, { boxShadow: '0 12px 50px 0 rgba(0, 0, 0, 0.2)', translateX: hoverOffset, duration: 0.2 });
        };

        const handleMouseLeave = () => {
            if (!frontBG.current || window.innerWidth < 768) return;
            gsap.to(frontBG.current, { boxShadow: 'none', translateX: 0, duration: 0.2 });
        };

        const handleClickOutside = (e) => {
            if (!container.contains(e.target)) {
                if (isToggled.current) {
                    collapse();
                }
            }
        };

        const handleKeyPress = (e) => {
            if (e.keyCode === 27 && isToggled.current) {
                collapse();
            }
        };

        if (container) {
            container.addEventListener('mouseenter', handleMouseEnter);
            container.addEventListener('mouseleave', handleMouseLeave);
            container.addEventListener('keydown', handleKeyPress);
            //Detect click outside
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            container.removeEventListener('mouseenter', handleMouseEnter);
            container.removeEventListener('mouseleave', handleMouseLeave);
            container.removeEventListener('keydown', handleKeyPress);
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    let statClass = 'stat';

    if (className) statClass = statClass + ' ' + className; //Custom classname for varying styles
    if (isFlipped) statClass = statClass + ' stat--flipped';

    const handleClick = () => {
        if (!isToggled.current) {
            expand();
        } else {
            collapse();
        }
        isToggled.current = !isToggled.current;
    };

    const expand = () => {
        if (typeof window === 'undefined') return;
        const viewportOffset = statContainer.current.getBoundingClientRect();

        //Keyboard navigation
        back.current.removeAttribute('tabindex');
        front.current.setAttribute('tabindex', -1);
        back.current.focus();

        var tl = gsap.timeline();
        const newIndex = window.lastIndex ? window.lastIndex + 2 : 5;
        window.lastIndex = newIndex;
        tl.to(back.current, { translateX: 0, opacity: 1, ease: 'back.out(1)', duration: 0.4 }, 0);
        gsap.set(statContainer.current, { zIndex: newIndex });

        //Calculate required height. Have to temporarily change the width to get this calculation right.
        //For the panel style, this applies to mobile only
        if (window.innerWidth < 768) {
            const heightIncrease = backInner.current.offsetHeight;
            tl.to(back.current, { height: heightIncrease }, 0.3);
        }

        //Scroll the view to fit the statistic
        const bottomPosition = viewportOffset.top + statContainer.current.offsetHeight;
        const offsetTop = statContainer.current.getBoundingClientRect().top + document.documentElement.scrollTop;
        const scrollPositionCentered = offsetTop - statContainer.current.offsetHeight / 2;
        if (bottomPosition > window.innerHeight) {
            gsap.to(window, { duration: 0.4, scrollTo: { y: scrollPositionCentered } });
        }

        //Quick bugfix for modal. Alternatively handle via more robust 'expandingAnotherStat' selector in statistic.js
        setTimeout(() => {
            tl.to('.modal-fade', { opacity: 0, duration: 0.3 }, 0);
            tl.to('.modal-fade', { display: 'none' }, 0.3);
        }, 300);
    };

    const collapse = () => {
        var tl = gsap.timeline();
        tl.to(back.current, { translateX: offset, opacity: 0, ease: 'back.out(1)', duration: 0.4 }, 0);
        //Deal with keyboard nav
        back.current.setAttribute('tabindex', -1);
        front.current.removeAttribute('tabindex');
    };

    return (
        <div className={statClass} style={style && style}>
            <div className="stat__wrap" ref={statContainer}>
                <button className="stat__front" onClick={handleClick} ref={front}>
                    <div className="stat__panel-bg" ref={frontBG}>
                        {/* As there are so many variations, bg image is determined by CSS class */}
                        <div className="stat__panel-bg-image"></div>
                    </div>
                    <div className="stat__content">
                        <div>
                            {icon && <div className="stat__icon">{icon}</div>}
                            <div className="stat__details" data-equ-numericcounter='{"value": 195, "duration": 1000, "divider": ""}'>
                                <strong className="u-block-element">
                                    <span>
                                        {showDollar && '$'}
                                        <span className="numeric-counter__value">{value && value} </span>
                                    </span>
                                    {unit && unit}
                                </strong>
                                {label && <p>{label}</p>}
                            </div>
                        </div>
                    </div>
                    <span className="u-sr-only">Button to open more info about {label}</span>
                </button>
                <button className="stat__back" onClick={handleClick} ref={back}>
                    <div ref={backInner}>
                        <div className="btn-close btn-close--big"></div>
                        <div className="stat__panel-bg"></div>
                        <div className="stat__back-header">
                            {icon && <div className="stat__back-icon">{icon}</div>}
                            <h2 className="stat__back-title">{title && title}</h2>
                        </div>
                        <div className="stat__back-main">
                            {description && typeof description === 'string' && <p>{description}</p>}
                            {description && typeof description !== 'string' && description}
                            {graphData && <BarGraph data={graphData} label={label} />}
                            {(tableDataPrevYear || tableDataCurrentYear) && (
                                <div className="table-responsive">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Period</th>
                                                <th>Actual</th>
                                                <th>Target</th>
                                                <th>Variance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableDataPrevYear && (
                                                <tr>
                                                    {tableDataPrevYear.map((value, index) => (
                                                        <td key={`${label.replace(' ', '-').toLowerCase() + 'tableCell' + index}`}>
                                                            {value}
                                                        </td>
                                                    ))}
                                                </tr>
                                            )}
                                            {tableDataCurrentYear && (
                                                <tr>
                                                    {tableDataCurrentYear.map((value, index) => (
                                                        <td key={`${label.replace(' ', '-').toLowerCase() + 'tableCell' + index}`}>
                                                            {value}
                                                        </td>
                                                    ))}
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                    <span className="u-sr-only">Button to close more info about {label}</span>
                </button>
            </div>
        </div>
    );
};
